import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { wishlistApi, cartApi, authTokenStorage } from '@/lib/api'

interface WishlistItem {
  id: number
  product_id: number
  product: {
    id: number
    name: string
    slug: string
    price: number
    compare_at_price?: number
    images: Array<{ url: string; alt_text?: string }>
    in_stock: boolean
  }
  created_at: string
}

interface Wishlist {
  items: WishlistItem[]
  total: number
}

export const Route = createFileRoute('/wishlist')({
  component: WishlistPage,
})

function WishlistPage() {
  const [wishlist, setWishlist] = useState<Wishlist | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const currencySymbol = '₹'

  useEffect(() => {
    const token = authTokenStorage.getAccessToken()
    setIsAuthenticated(!!token)

    if (token) {
      loadWishlist()
    } else {
      setLoading(false)
    }
  }, [])

  const loadWishlist = async () => {
    try {
      const data = await wishlistApi.get() as Wishlist
      setWishlist(data)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load wishlist'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const removeFromWishlist = async (productId: number) => {
    try {
      await wishlistApi.remove(productId)
      await loadWishlist()
    } catch (err) {
      console.error('Failed to remove from wishlist:', err)
    }
  }

  const addToCart = async (productId: number) => {
    try {
      await cartApi.addItem(productId, 1)
      // Optionally remove from wishlist after adding to cart
      await removeFromWishlist(productId)
    } catch (err) {
      console.error('Failed to add to cart:', err)
    }
  }

  if (!isAuthenticated) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold mb-4">Your Wishlist</h1>
          <p className="text-gray-600 mb-8">Sign in to view and manage your wishlist</p>
          <Link
            to="/login"
            className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700"
          >
            Sign In
          </Link>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">My Wishlist</h1>
        <p className="text-gray-600">Loading...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">My Wishlist</h1>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    )
  }

  if (!wishlist || wishlist.items.length === 0) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold mb-4">Your Wishlist is Empty</h1>
          <p className="text-gray-600 mb-8">Save items you love by clicking the heart icon</p>
          <Link
            to="/products"
            search={{}}
            className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700"
          >
            Browse Products
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">My Wishlist</h1>
        <span className="text-gray-600">{wishlist.total} items</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {wishlist.items.map((item) => (
          <div key={item.id} className="bg-white rounded-lg border overflow-hidden group">
            <Link to="/products/$slug" params={{ slug: item.product.slug }} className="block relative aspect-square">
              {item.product.images && item.product.images.length > 0 ? (
                <img
                  src={item.product.images[0].url}
                  alt={item.product.images[0].alt_text || item.product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                />
              ) : (
                <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                  <span className="text-gray-400">No image</span>
                </div>
              )}

              {/* Sale badge */}
              {item.product.compare_at_price && item.product.compare_at_price > item.product.price && (
                <span className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 text-xs font-medium rounded">
                  Sale
                </span>
              )}

              {/* Remove button */}
              <button
                onClick={(e) => {
                  e.preventDefault()
                  removeFromWishlist(item.product_id)
                }}
                className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full shadow flex items-center justify-center hover:bg-red-50 transition-colors"
              >
                <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
            </Link>

            <div className="p-4">
              <Link to="/products/$slug" params={{ slug: item.product.slug }} className="block">
                <h3 className="font-medium text-gray-900 mb-2 line-clamp-2 hover:text-blue-600">
                  {item.product.name}
                </h3>
              </Link>

              <div className="flex items-center gap-2 mb-4">
                <span className="font-bold text-lg">
                  {currencySymbol}{item.product.price.toLocaleString()}
                </span>
                {item.product.compare_at_price && item.product.compare_at_price > item.product.price && (
                  <span className="text-gray-500 line-through text-sm">
                    {currencySymbol}{item.product.compare_at_price.toLocaleString()}
                  </span>
                )}
              </div>

              <button
                onClick={() => addToCart(item.product_id)}
                disabled={!item.product.in_stock}
                className="w-full py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {item.product.in_stock ? 'Add to Cart' : 'Out of Stock'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
